import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { MultiSelect, Select, Text } from "@mantine/core";

function candidateKey(candidate) {
  if (candidate.organization_user_admin_id) {
    return `admin:${candidate.organization_user_admin_id}`;
  }
  return `manager:${candidate.organization_user_manager_id}`;
}

function candidateLabel(candidate, groupLabel) {
  const name = `${candidate.first_name || ""} ${candidate.last_name || ""}`.trim();
  const email = candidate.email || "";
  const variety = candidate.staff_variety || "";
  const base = name
    ? `${name}${email ? ` (${email})` : ""}`
    : email || candidate.user_id;
  return `${groupLabel}: ${base} · ${variety}`;
}

function parseKeys(keys) {
  const organization_user_admin_ids = [];
  const organization_user_manager_ids = [];
  (keys || []).forEach((key) => {
    const [variety, id] = String(key).split(":");
    if (variety === "admin" && id) {
      organization_user_admin_ids.push(id);
    } else if (variety === "manager" && id) {
      organization_user_manager_ids.push(id);
    }
  });
  return { organization_user_admin_ids, organization_user_manager_ids };
}

function parseSingleKey(key) {
  if (!key) return null;
  const [variety, id] = String(key).split(":");
  if (variety === "admin" && id) {
    return { organization_user_admin_id: id };
  }
  if (variety === "manager" && id) {
    return { organization_user_manager_id: id };
  }
  return null;
}

/**
 * @param {{
 *   organizationId: string,
 *   userId: string|null,
 *   mode?: "multi" | "single",
 *   value?: string[] | string | null,
 *   onChange: (value: any) => void,
 *   excludeKeys?: string[],
 *   label?: string,
 * }} props
 */
export default function AssessmentViewerPicker({
  organizationId,
  userId,
  mode = "multi",
  value = mode === "multi" ? [] : null,
  onChange,
  excludeKeys = [],
  label,
}) {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState({ managing: [], other: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!organizationId || !userId) {
      setCandidates({ managing: [], other: [] });
      return;
    }

    setLoading(true);
    axios
      .post(`/organizations/${organizationId}/assessment-viewer-candidates/`, {
        user_id: userId,
        scope: "all",
      })
      .then(({ data }) => {
        const payload = data.response[0] || {};
        setCandidates({
          managing: payload.managing || [],
          other: payload.other || [],
        });
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setCandidates({ managing: [], other: [] });
        toast.error(err);
      });
  }, [organizationId, userId]);

  const excludeSet = useMemo(
    () => new Set((excludeKeys || []).map(String)),
    [excludeKeys]
  );

  const options = useMemo(() => {
    const managingLabel = t("assessment_viewer_managing");
    const otherLabel = t("assessment_viewer_other");
    const managingItems = [];
    const otherItems = [];

    (candidates.managing || []).forEach((candidate) => {
      const key = candidateKey(candidate);
      if (excludeSet.has(key)) return;
      managingItems.push({
        value: key,
        label: candidateLabel(candidate, managingLabel),
      });
    });

    (candidates.other || []).forEach((candidate) => {
      const key = candidateKey(candidate);
      if (excludeSet.has(key)) return;
      otherItems.push({
        value: key,
        label: candidateLabel(candidate, otherLabel),
      });
    });

    const groups = [];
    if (managingItems.length) {
      groups.push({ group: managingLabel, items: managingItems });
    }
    if (otherItems.length) {
      groups.push({ group: otherLabel, items: otherItems });
    }
    return groups;
  }, [candidates, excludeSet, t]);

  if (!userId) {
    return (
      <Text size="sm" c="dimmed">
        {t("assessment_viewer_select_assignee_first")}
      </Text>
    );
  }

  if (mode === "single") {
    return (
      <Select
        label={label || t("assessment_viewer_add")}
        searchable
        clearable
        data={options || []}
        value={value || null}
        onChange={(key) => onChange(key)}
        disabled={loading}
        nothingFoundMessage={t("not_found")}
      />
    );
  }

  return (
    <MultiSelect
      label={label || t("assessment_viewer_plural")}
      searchable
      clearable
      data={options || []}
      value={Array.isArray(value) ? value : []}
      onChange={(keys) => {
        onChange({
          keys: keys || [],
          ...parseKeys(keys),
        });
      }}
      disabled={loading}
      nothingFoundMessage={t("not_found")}
    />
  );
}

export { candidateKey, parseKeys, parseSingleKey };
